import { FC, useEffect, useState } from "react";
import { Flex, Button, Text, Icon } from "@smile2impress/components/relaunch";
import { ChevronDownSmallIcon } from "@smile2impress/components";
import { useRouter } from "next/router";
import { useGoToBooking } from "hooks/useGoToBooking";
import { useUTSLocaleContext } from "context/utsLocaleContext";

import Link from "components/Link";
import { isHeaderNavLinkActive } from "utils/isHeaderNavLinkActive";

import { MenuItemProps } from "./MenuItem.types";

export const MenuItem: FC<MenuItemProps> = ({
    text,
    link,
    items = [],
    cta,
    index,
    activeIndex = -1,
    onToggleMenu,
    highlighted = false,
}) => {
    const collapsable = Boolean(items.length && !link);
    const { goToBooking } = useGoToBooking();
    const router = useRouter();
    const { locale } = useUTSLocaleContext();
    const isActiveNavLink = link
        ? isHeaderNavLinkActive(router, link, locale)
        : false;

    const [isOpen, setOpen] = useState<boolean>(false);
    const [otherItemIsActive, setOtherItemIsActive] = useState<boolean>(
        activeIndex !== index && activeIndex >= 0
    );

    if (!cta.onClick && !cta.link) {
        cta.onClick = goToBooking;
    }

    const handleToggleMenu = () => {
        setOpen((prev) => {
            const newVal = !prev;
            if (typeof index !== "undefined" && onToggleMenu)
                onToggleMenu({
                    index,
                    items,
                    isOpen: newVal,
                });
            return newVal;
        });
    };

    useEffect(() => {
        if (activeIndex !== index) {
            setOpen(false);
            setOtherItemIsActive(activeIndex !== index && activeIndex >= 0);
        } else {
            setOpen(true);
            setOtherItemIsActive(false);
        }
    }, [activeIndex]);

    return (
        <Flex direction="column">
            <Button
                height={["36px"]}
                variant={
                    highlighted
                        ? otherItemIsActive
                            ? "white"
                            : "black"
                        : "ghost"
                }
                justifyContent="space-between"
                display="flex"
                px={highlighted ? 1 : 0}
                {...(collapsable && {
                    onClick: handleToggleMenu,
                })}
                {...(!collapsable && {
                    as: Link,
                    href: link,
                })}
                opacity={otherItemIsActive ? 0.4 : 1}
                {...(isActiveNavLink && {
                    color: "base.blue",
                })}
            >
                <Text
                    as="span"
                    textStyle="captionSmall"
                    color={
                        highlighted && !otherItemIsActive
                            ? "text.baseInverse"
                            : "inherit"
                    }
                    _dark={{
                        color:
                            highlighted && !otherItemIsActive
                                ? "text.base"
                                : "inherit",
                    }}
                    fontWeight="bold"
                >
                    {text}
                </Text>
                {collapsable && (
                    <Icon
                        w={1}
                        h={1}
                        color={
                            highlighted && !otherItemIsActive
                                ? "text.baseInverse"
                                : "inherit"
                        }
                        _dark={{
                            color:
                                highlighted && !otherItemIsActive
                                    ? "text.base"
                                    : "inherit",
                        }}
                        as={ChevronDownSmallIcon}
                        transition="all 0.1s ease-in"
                        ml="8px"
                        transform={`rotate(${isOpen ? -180 : 0}deg)`}
                    />
                )}
            </Button>
        </Flex>
    );
};
